import { platforms_route, sync_toggle_route, VITE_API_URL } from "@/constants";
import type { AuthFetchParams } from "./global";
import { isTokenInvalid } from "@/lib/api-client";
import { queryOptions, useMutation } from "@tanstack/react-query";
import type { Platform } from "@/lib/types";
import { useAuth } from "@/providers/auth";
import { toast } from "sonner";
import i18n from "@/i18n";
import { queryClient } from "@/main";
import { useOrg } from "@/providers/organization";

async function getPlatforms(params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${platforms_route}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as Platform[];
}

export const platformsQueryOpts = (params: AuthFetchParams) =>
  queryOptions({
    queryKey: [params.orgId, "platforms"],
    queryFn: async () => await getPlatforms(params),
  });

export const usePlatformSyncToggle = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();

  return useMutation({
    mutationFn: async (pcId: string) => {
      const res = await fetch(`${VITE_API_URL}${sync_toggle_route}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
        body: JSON.stringify({
          pc_id: pcId,
        }),
      });

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const access = res.headers.get("access-token");
      if (access) setToken(access);
      // don't really care about the returned data
    },
    onSuccess: () => {
      toast.success(i18n.t("toast.syncStateUpdated"));
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
    onError: () => {
      toast.error(i18n.t("toast.syncStateFailed"));
    },
  });
};
